import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import type { Pricing, RentedSubscription, Subscription } from "common/types";

import { post } from "../helpers/HTTP";

async function workspaceEstimateUpdateRequest(
  workspaceId: string,
  subscriptionId: string,
  planId: string,
  addonId: string,
  quantity: number,
  prorate: boolean,
): Promise<{ amountDue: number; nextBillingAt: number; pricePerItem: number }> {
  const { estimate } = await post(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/estimates/update`,
    {
      items: [
        {
          id: planId,
        },
        ...(quantity > 0
          ? [
              {
                id: addonId,
                quantity,
              },
            ]
          : []),
      ],
      prorate,
    },
  );

  const { subscription_estimate: subscriptionEstimate } = estimate;

  const invoiceEstimate =
    estimate.invoice_estimate || estimate.next_invoice_estimate;

  return {
    amountDue: invoiceEstimate.amount_due,
    nextBillingAt: subscriptionEstimate.next_billing_at,
    pricePerItem: invoiceEstimate.line_items.find(
      ({ pricing_model }: { pricing_model: string }) =>
        pricing_model === "per_unit",
    ).unit_amount,
  };
}

export default function useWorkspaceEstimateUpdate({
  subscription,
  pricing,
  quantity,
  prorate,
}: {
  subscription?: Subscription | RentedSubscription;
  pricing: { addon: Pricing; plan: Pricing };
  quantity: number;
  prorate: boolean;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { addon, plan } = pricing;

  const {
    data: estimateUpdate,
    isFetching: isLoadingEstimateUpdate,
    error: estimateUpdateError,
  } = useQuery({
    queryKey: [
      "estimateUpdate",
      workspaceId,
      subscription ? subscription.id : "no subscription",
      pricing,
      quantity,
      prorate,
    ],
    queryFn: () =>
      workspaceEstimateUpdateRequest(
        workspaceId,
        subscription?.id,
        plan.id,
        addon.id,
        quantity,
        prorate,
      ),
    enabled: !!subscription, // The request should only be called if there is a subscription
  });

  return { estimateUpdate, isLoadingEstimateUpdate, estimateUpdateError };
}
